import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Radio, Calendar as CalendarIcon, Mic, ChevronLeft, ChevronRight } from "lucide-react";

function getEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/)([^&?/]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
  const twitch = url.match(/twitch\.tv\/([^/?]+)/);
  if (twitch) return `https://player.twitch.tv/?channel=${twitch[1]}&parent=${window.location.hostname}`;
  return url;
}

const HOURS = Array.from({length: 24}, (_, i) => i); // 00:00 to 23:00
const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const DOW = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

function toKey(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function isSameDay(a, b) { return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate(); }

export default function Podcast() {
  const { content } = useOutletContext() || {};
  const c = content || {};
  const [eps, setEps] = useState([]);
  const [availability, setAvailability] = useState({ blocks: [], scheduled: [], booked: [] });
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [form, setForm] = useState({ guest_name:"", guest_email:"", topic:"" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/podcast/episodes").then(r => setEps(r.data));
    api.get("/podcast/availability").then(r => setAvailability(r.data)).catch(() => {});
  }, []);

  const live = eps.find(e => e.live);
  const past = eps.filter(e => !e.live);

  // Compute slot state for a given hour on a day
  const slotState = (day, hour) => {
    const slotStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0);
    const slotEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour+1, 0);
    // Blocks
    for (const b of availability.blocks || []) {
      const bs = new Date(b.start), be = new Date(b.end);
      if (slotStart < be && slotEnd > bs) return { state: "blocked", label: b.label };
    }
    // Scheduled episodes
    for (const s of availability.scheduled || []) {
      const ss = new Date(s.start);
      if (isSameDay(ss, day) && ss.getHours() === hour) return { state: "scheduled", label: s.title };
    }
    // Confirmed bookings
    for (const b of availability.booked || []) {
      const bs = new Date(b.start);
      if (isSameDay(bs, day) && bs.getHours() === hour) return { state: "booked", label: "Prenotato" };
    }
    // Past slots
    if (slotStart < new Date()) return { state: "past" };
    return { state: "free" };
  };

  // Aggregate state per day for calendar markers
  const dayMarker = (day) => {
    let sched = 0, blocked = 0, booked = 0, free = 0, past = 0;
    for (const h of HOURS) {
      const s = slotState(day, h).state;
      if (s === "scheduled") sched++;
      else if (s === "blocked") blocked++;
      else if (s === "booked") booked++;
      else if (s === "past") past++;
      else free++;
    }
    return { sched, blocked, booked, free, past };
  };

  const daysGrid = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startDow = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(month.getFullYear(), month.getMonth()+1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const book = async () => {
    if (!selectedDate || selectedHour === null) return toast.error("Scegli giorno e orario");
    if (!form.guest_name || !form.guest_email || !form.topic) return toast.error("Compila tutti i campi");
    const iso = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), selectedHour, 0).toISOString();
    setBusy(true);
    try {
      await api.post("/podcast/slots", { date: iso, ...form });
      toast.success("Slot richiesto! Ti ricontatteremo per confermare.");
      setForm({ guest_name:"", guest_email:"", topic:"" });
      setSelectedHour(null);
      api.get("/podcast/availability").then(r => setAvailability(r.data));
    } catch { toast.error("Errore prenotazione"); }
    setBusy(false);
  };

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <div className="mb-10">
        <div className="tag-pill mb-3"><Radio size={12}/> Podcast studio</div>
        <h1 className="font-display text-5xl sm:text-6xl uppercase text-white" data-testid="podcast-heading">{c.podcast_title || "Podcast Flow"}</h1>
        <p className="mt-3 text-white/70 max-w-2xl whitespace-pre-line">{c.podcast_subtitle || ""}</p>
      </div>

      {live ? (
        <div className="card p-6 mb-10" data-testid="podcast-live">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-3 h-3 rounded-full bg-white live-dot"></span>
            <span className="font-mono text-xs uppercase tracking-widest text-white">In diretta ora</span>
          </div>
          <h2 className="font-display text-3xl uppercase mb-4">{live.title}</h2>
          {live.stream_url && (
            <div className="aspect-video">
              <iframe src={getEmbedUrl(live.stream_url)} title={live.title}
                className="w-full h-full border border-[#FFFFFF]"
                allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen/>
            </div>
          )}
        </div>
      ) : (
        <div className="card p-8 mb-10 text-center" data-testid="podcast-nolive">
          <Radio size={32} className="mx-auto text-white/40"/>
          <div className="mt-3 font-display text-2xl uppercase text-white">Nessuna diretta ora</div>
          <p className="text-white/50 text-sm mt-1">Torna presto o guarda l'archivio qui sotto.</p>
        </div>
      )}

      {/* Prenota il tuo slot */}
      <div className="mb-14">
        <h2 className="font-display text-3xl uppercase mb-1">Prenota il tuo slot</h2>
        <p className="text-white/70 text-sm mb-6">Vuoi essere nostro ospite? Prenotati!</p>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-3">
            <div className="card p-5">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth()-1, 1))}
                  className="w-9 h-9 flex items-center justify-center border border-white/40 hover:bg-white hover:text-black transition-colors" data-testid="cal-prev">
                  <ChevronLeft size={16}/>
                </button>
                <div className="font-display text-xl uppercase tracking-widest text-white" data-testid="cal-month">
                  {MONTHS[month.getMonth()]} {month.getFullYear()}
                </div>
                <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth()+1, 1))}
                  className="w-9 h-9 flex items-center justify-center border border-white/40 hover:bg-white hover:text-black transition-colors" data-testid="cal-next">
                  <ChevronRight size={16}/>
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {DOW.map(d => <div key={d} className="text-center text-[10px] font-mono uppercase tracking-widest text-white/50 py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {daysGrid.map((day, i) => {
                  if (!day) return <div key={i}/>;
                  const isPast = day < new Date(new Date().setHours(0,0,0,0));
                  const isToday = isSameDay(day, new Date());
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const mk = dayMarker(day);
                  const allBlocked = isPast || (mk.free === 0 && mk.past === 0);
                  return (
                    <button key={i} onClick={() => { if (!isPast) { setSelectedDate(day); setSelectedHour(null); } }}
                      disabled={isPast}
                      data-testid={`cal-day-${toKey(day)}`}
                      className={`aspect-square flex flex-col items-center justify-center border text-sm transition-colors relative
                        ${isSelected ? "bg-white text-black border-white" :
                          isPast ? "border-white/10 text-white/20 cursor-not-allowed" :
                          allBlocked ? "border-white/20 text-white/40" :
                          "border-white/40 text-white hover:bg-white/10"}
                      `}>
                      <span className={`font-mono ${isToday && !isSelected ? "underline underline-offset-2" : ""}`}>{day.getDate()}</span>
                      {!isPast && (mk.sched > 0 || mk.booked > 0 || mk.blocked > 0) && (
                        <div className="flex gap-0.5 mt-0.5">
                          {mk.sched > 0 && <span className="w-1 h-1 rounded-full bg-white"/>}
                          {mk.booked > 0 && <span className={`w-1 h-1 rounded-full ${isSelected ? "bg-black" : "bg-white/60"}`}/>}
                          {mk.blocked > 0 && <span className={`w-1 h-1 rounded-full ${isSelected ? "bg-black/60" : "bg-white/30"}`}/>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-4 flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-widest text-white/60">
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white"/> Programmato</div>
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white/60"/> Prenotato</div>
                <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-white/30"/> Bloccato</div>
              </div>
            </div>
          </div>

          {/* Slot picker + form */}
          <div className="lg:col-span-2">
            <div className="card p-5">
              {selectedDate ? (
                <>
                  <div className="text-xs font-mono uppercase tracking-widest text-white/60 mb-1">Giorno selezionato</div>
                  <div className="font-display text-2xl uppercase text-white mb-4">
                    {selectedDate.toLocaleDateString("it-IT", { day:"2-digit", month:"long", year:"numeric" })}
                  </div>
                  <div className="text-xs font-mono uppercase tracking-widest text-white/60 mb-2">Scegli l'orario</div>
                  <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1 mb-4">
                    {HOURS.map(h => {
                      const st = slotState(selectedDate, h);
                      const disabled = ["blocked","scheduled","booked","past"].includes(st.state);
                      const active = selectedHour === h;
                      return (
                        <button key={h} onClick={() => !disabled && setSelectedHour(h)} disabled={disabled}
                          data-testid={`slot-${h}`}
                          title={st.label || ""}
                          className={`py-2 text-sm font-mono border transition-colors
                            ${active ? "bg-white text-black border-white" :
                              disabled ? "border-white/15 text-white/30 line-through cursor-not-allowed" :
                              "border-white/40 text-white hover:bg-white/10"}`}>
                          {String(h).padStart(2,"0")}:00
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid gap-2">
                    <input placeholder="Nome" value={form.guest_name} onChange={e => setForm({...form, guest_name: e.target.value})}
                      className="bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="slot-name"/>
                    <input placeholder="Email" type="email" value={form.guest_email} onChange={e => setForm({...form, guest_email: e.target.value})}
                      className="bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="slot-email"/>
                    <textarea placeholder="Di cosa parleremo?" rows={3} value={form.topic} onChange={e => setForm({...form, topic: e.target.value})}
                      className="bg-black border border-white/40 px-3 py-2 rounded text-white" data-testid="slot-topic"/>
                    <button onClick={book} disabled={busy || selectedHour === null} className="brutal-btn brutal-btn-primary" data-testid="slot-submit">
                      <CalendarIcon size={16}/> {busy ? "Invio..." : "Prenotati"}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-10">
                  <CalendarIcon size={36} className="mx-auto text-white/30"/>
                  <div className="mt-3 font-display text-xl uppercase text-white/50">Scegli un giorno</div>
                  <p className="text-white/40 text-sm mt-1">Seleziona una data dal calendario per vedere gli orari disponibili.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Archivio */}
      <div>
        <h2 className="font-display text-3xl uppercase mb-4">Archivio episodi</h2>
        {past.length === 0 && <div className="text-white/50">Nessun episodio ancora pubblicato.</div>}
        <div className="grid md:grid-cols-2 gap-4">
          {past.map(ep => (
            <div key={ep.id} className="card p-4" data-testid={`ep-${ep.id}`}>
              <div className="flex items-center gap-2 text-xs font-mono uppercase text-white/50 tracking-widest">
                <Mic size={12}/> Episodio
              </div>
              <div className="font-display text-2xl uppercase mt-1">{ep.title}</div>
              <p className="text-sm text-white/70 mt-1">{ep.description}</p>
              {ep.stream_url && (
                <div className="mt-3 aspect-video">
                  <iframe src={getEmbedUrl(ep.stream_url)} title={ep.title} className="w-full h-full border border-[#FFFFFF]"
                    allow="autoplay; encrypted-media" allowFullScreen/>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
